import { Link } from 'react-router-dom';
import type { ProfileHeaderData } from '../../lib/types';
import { Badge } from '../badges/Badge';
import { Ribbon } from '../ribbons/Ribbon';
import { MotifLayer } from '../badges/badgeArt';
import { Bar } from '../ui';
import { formatNumber, formatDate } from '../../lib/format';

/**
 * The right-hand side of the profile header.
 *
 * The problem this solves is specific: the top of a profile was an identity
 * column beside a large blank area, and everything worth putting there — the
 * badge cabinet, the ribbons — was instead given its own full-width section
 * further down. So a profile was tall, mostly empty at the top, and dense in
 * places nobody had scrolled to.
 *
 * This is the fix, and the rule behind it is one question: *can this fit in
 * space that already exists?* Badges, ribbons, accolades and four numbers all
 * can, so they do, and the giant sections they used to occupy are gone rather
 * than duplicated. What is left in those systems is a "View all" link, because
 * the full Badges page and the per-Pokemon ribbon views are still where you go
 * to actually study them.
 *
 * ## Density without cramming
 *
 * Three stacked rows — badges, ribbons, then accolades and stats sharing a line
 * — inside a grid that gives the identity column about a third of the width.
 * Each row is its own block with a heading and a link out, so the density comes
 * from *filling* the area rather than from shrinking anything. Nothing here
 * carries descriptive text: a badge shows its artwork, name and tier, and the
 * description lives in the tooltip.
 *
 * ## Nothing renders empty
 *
 * Every block falls back on the server — pinned first, then the best of what
 * the player has. A block reaches this component empty only for a genuinely new
 * trainer, and then it renders a one-line prompt rather than a hole.
 */

export interface ProfileHeaderProps {
  header: ProfileHeaderData;
  username: string;
  isSelf: boolean;
  /**
   * How the featured badges are arranged.
   *
   * `cabinet` shows artwork with name and tier, `grid` is artwork only and
   * fits twice as many, `row` is a single scrolling line. Stored on
   * `ProfileCustomization.badgeLayout`, which used to arrange the full-width
   * Badge Cabinet — when that section left the profile the column became a
   * control that changed nothing, so it was pointed at the block that replaced
   * it rather than left lying as a switch that lies.
   */
  badgeLayout?: 'cabinet' | 'grid' | 'row';
  /** Living Trainers chase nothing, so their header carries none of this. */
  hidden?: boolean;
}

export function ProfileHeaderPanels({
  header,
  username,
  isSelf,
  badgeLayout = 'cabinet',
  hidden,
}: ProfileHeaderProps) {
  if (hidden) return null;

  return (
    <div className="phead__blocks">
      <BadgeBlock header={header} username={username} isSelf={isSelf} layout={badgeLayout} />
      <RibbonBlock header={header} username={username} isSelf={isSelf} />

      {/* Accolades and stats share the bottom row: three chips and four numbers
          are each too small to justify a row of their own, and side by side
          they read as one line about what somebody has finished and how much
          they have done. */}
      <div className="phead__row phead__row--split">
        <AccoladeBlock header={header} isSelf={isSelf} />
        <StatBlock header={header} username={username} />
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------
   Badges
   ------------------------------------------------------------------------- */

function BadgeBlock({
  header,
  username,
  isSelf,
  layout,
}: Omit<ProfileHeaderProps, 'hidden' | 'badgeLayout'> & { layout: 'cabinet' | 'grid' | 'row' }) {
  return (
    <section className="phead__block">
      <BlockHead
        label="Featured Badges"
        note={header.usingDefaults.badges ? 'Top badges' : undefined}
        count={header.totals.badges}
        to={isSelf ? '/badges' : `/badges?trainer=${username}`}
        action="View all"
      />

      {header.badges.length === 0 ? (
        <Prompt>
          {isSelf
            ? 'Badges track the long game — types collected, eggs hatched, trainers helped. Every one is already counting.'
            : 'No badges yet.'}
        </Prompt>
      ) : (
        <div className={`phead__badges phead__badges--${layout}`}>
          {header.badges.map((badge, index) => (
            <div
              key={badge.key}
              className="fbadge"
              // Staggered so the max-tier foil sweeps do not all fire on the
              // same frame, which reads as a flash rather than as shine.
              style={{ ['--badge-delay' as string]: `${(index * 0.4).toFixed(1)}s` }}
              title={`${badge.name}${badge.tierName ? ` — ${badge.tierName}` : ''}\n${badge.description}${
                badge.nextGoal
                  ? `\n${formatNumber(badge.progress)} / ${formatNumber(badge.nextGoal)} to the next tier`
                  : ''
              }`}
            >
              <Badge badge={badge} size={56} />
              <span className="fbadge__text">
                <span className="fbadge__name">{badge.name}</span>
                {badge.tierName && <span className="fbadge__tier">{badge.tierName}</span>}
                {/* Progress only where there is somewhere left to go. A
                    finished badge showing "500 / 500" reads as unfinished. */}
                {badge.nextGoal !== null && (
                  <span className="fbadge__prog mono">
                    {formatNumber(badge.progress)} / {formatNumber(badge.nextGoal)}
                  </span>
                )}
              </span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

/* -------------------------------------------------------------------------
   Ribbons
   ------------------------------------------------------------------------- */

/**
 * Featured ribbons.
 *
 * The one thing this block must never do is read as a second list of badges.
 * A ribbon belongs to a *Pokemon*, so every card names the Pokemon and links to
 * it — that is the whole difference between "this trainer has hatched a lot of
 * eggs" and "this Eevee is a proven parent from a forty-second-generation
 * line", and it is what makes ribbons personal.
 */
function RibbonBlock({ header, username, isSelf }: Omit<ProfileHeaderProps, 'hidden'>) {
  return (
    <section className="phead__block">
      <BlockHead
        label="Featured Ribbons"
        note={header.usingDefaults.ribbons ? 'Most recent' : undefined}
        count={header.totals.ribbons}
        to={isSelf ? '/companions' : `/companions/${username}`}
        action="View ribbons"
      />

      {header.ribbons.length === 0 ? (
        <Prompt>
          {isSelf
            ? 'Your Pokémon earn ribbons as they grow — for being fed, for being bred from, for what they live through.'
            : 'No ribbons yet.'}
        </Prompt>
      ) : (
        <div className="phead__ribbons">
          {header.ribbons.map((ribbon) => (
            <Link
              key={ribbon.id}
              to={`/pokemon/${ribbon.pokemonId}`}
              className="fribbon"
              title={`${ribbon.name}\n${ribbon.description}\nWorn by ${ribbon.on} · earned ${formatDate(ribbon.awardedAt)}${
                ribbon.note ? `\n${ribbon.note}` : ''
              }`}
            >
              <Ribbon ribbon={ribbon} size={34} />
              <span className="fribbon__text">
                <span className="fribbon__name">{ribbon.name}</span>
                <span className="fribbon__on">{ribbon.on}</span>
              </span>
            </Link>
          ))}
        </div>
      )}
    </section>
  );
}

/* -------------------------------------------------------------------------
   Accolades
   ------------------------------------------------------------------------- */

function AccoladeBlock({ header, isSelf }: { header: ProfileHeaderData; isSelf: boolean }) {
  return (
    <section className="phead__block phead__block--half">
      <BlockHead label="Accolades" note={header.usingDefaults.accolades ? 'Best so far' : undefined} />

      {header.accolades.length === 0 ? (
        <Prompt>
          {isSelf ? 'Finish a Pokédex, a museum wing or a badge ladder.' : 'Nothing finished yet.'}
        </Prompt>
      ) : (
        <div className="phead__accolades">
          {header.accolades.map((accolade) => (
            <span
              key={accolade.key}
              className="accolade"
              style={{ ['--accolade-accent' as string]: accolade.accent }}
              title={`${accolade.description}${accolade.detail ? `\n${accolade.detail}` : ''}`}
            >
              <svg viewBox="0 0 16 16" width={18} height={18} shapeRendering="crispEdges" aria-hidden>
                <MotifLayer motif={accolade.motif} fill="currentColor" highlight="#fff" />
              </svg>
              <span className="accolade__label">{accolade.label}</span>
            </span>
          ))}
        </div>
      )}
    </section>
  );
}

/* -------------------------------------------------------------------------
   Stats
   ------------------------------------------------------------------------- */

/**
 * Four numbers, formatted by the server's own catalog.
 *
 * `format` arrives on the payload rather than being decided here, so a stat
 * added to `game/statistics.ts` next month renders correctly on a client built
 * today — the same reason badge artwork crosses the wire as data.
 */
function StatBlock({ header, username }: { header: ProfileHeaderData; username: string }) {
  return (
    <section className="phead__block phead__block--half">
      <BlockHead label="Highlights" to={`/statistics/${username}`} action="All statistics" />
      <div className="phead__stats">
        {header.stats.map((stat) => (
          <div key={stat.key} className="fstat">
            <span className="fstat__value mono">{formatStat(stat.value, stat.format)}</span>
            <span className="fstat__label">{stat.label}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

function formatStat(value: number | string | null, format: string): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'string') return value || '—';
  switch (format) {
    case 'percent':
      return `${Math.round(value)}%`;
    case 'coins':
      return formatNumber(value);
    case 'duration': {
      // Minutes on the wire. Hours is the unit somebody actually thinks in.
      const hours = Math.round(value / 60);
      return hours >= 1 ? `${formatNumber(hours)}h` : `${Math.round(value)}m`;
    }
    default:
      return formatNumber(value);
  }
}

/* -------------------------------------------------------------------------
   Shared pieces
   ------------------------------------------------------------------------- */

/**
 * A block heading: label on the left, count and way out on the right.
 *
 * `note` says "these were chosen for you" without a sentence — a profile that
 * has not been arranged should not look broken, but its owner should be able to
 * tell at a glance that the editor would change it.
 */
function BlockHead({
  label,
  note,
  count,
  to,
  action,
}: {
  label: string;
  note?: string;
  count?: number;
  to?: string;
  action?: string;
}) {
  return (
    <div className="phead__blockhead">
      <span className="tiny mono muted">{label.toUpperCase()}</span>
      <span className="phead__blockhead-right">
        {note && <span className="tiny muted phead__auto">{note}</span>}
        {count !== undefined && count > 0 && <span className="tiny muted mono">{formatNumber(count)}</span>}
        {to && action && (
          <Link className="tiny phead__more" to={to}>
            {action} →
          </Link>
        )}
      </span>
    </div>
  );
}

/** The compact empty state. One line, never a panel-sized illustration. */
function Prompt({ children }: { children: React.ReactNode }) {
  return <p className="tiny muted phead__prompt">{children}</p>;
}

/**
 * The trainer EXP bar, extracted so the identity column can stay readable.
 *
 * Exported because the profile page composes the identity column itself — it
 * carries NPC detail this file has no business knowing about — and this is the
 * one piece of it worth sharing with the editor preview.
 */
export function TrainerExp({
  level,
  exp,
  needed,
  ratio,
}: {
  level: number;
  exp: number;
  needed: number;
  ratio: number;
}) {
  return (
    <div className="phead__exp">
      <div className="row row--between tiny mono">
        {/* The level leads, because it is the number somebody reads. "Trainer
            EXP" as a label plus a level chip somewhere else was two elements
            saying one thing. */}
        <strong>Lv {level}</strong>
        <span className="muted">
          {formatNumber(exp)} / {formatNumber(needed)} EXP
        </span>
      </div>
      <Bar ratio={ratio} variant="exp" />
    </div>
  );
}
